import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "flex items-center space-x-4" }
const _hoisted_4 = { class: "grid grid-cols-1 gap-4 md:grid-cols-4" }
const _hoisted_5 = { class: "flex flex-col justify-end pb-2.5" }

import { computed, onMounted, reactive, Ref } from 'vue';
import { useRoute } from 'vue-router';

import { useUiStore } from '@/stores';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';

import { required, maxLength } from '@/utils/validators';
import { toast, toastServiceError } from '@/services/core/notification';
import { goBack } from '@/composables/useNavigation';

import ConnectFlowService from '@/services/api/ConnectFlowService';

import {
    ConnectFlowFormState,
    ConnectFlowModel,
    ConnectFlowServiceCreateResult,
    DefaultServiceResult,
    FormAction,
    Permission,
} from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConnectFlowFormPage',
  props: {
    action: {
        type: String,
        default: 'view',
    },
},
  setup(__props) {

const route = useRoute();
const uiStore = useUiStore();

const props = __props;

const formState = reactive<ConnectFlowFormState>({
    name: null,
    isActive: true,
});

const rules = computed<ValidationArgs<ConnectFlowFormState>>(() => ({
    name: {
        required,
        maxLength: maxLength(500),
    },
    isActive: {
        required,
    },
}));

const v$: Ref<Validation<ValidationArgs<ConnectFlowFormState>>> = useVuelidate(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchConnectFlow = async () => {
    if (props.action !== FormAction.EDIT) return;

    const id = Number(route.params.id);

    try {
        setLoading(true);
        const response = await ConnectFlowService.get<ConnectFlowModel>(id);
        const { name, isActive } = response.data;
        formState.name = name;
        formState.isActive = isActive;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const onSubmit = async () => {
    const isFormCorrect = await v$.value.$validate();
    if (!isFormCorrect) return;

    setLoading(true);

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({
            name: 'ViewConnectFlowById',
            params: { id },
        });
    };

    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const response = await ConnectFlowService.update<
                DefaultServiceResult,
                ConnectFlowFormState
            >(id, formState);
            handleServiceSuccess(response.data.message, id);
        } else {
            const response = await ConnectFlowService.create<
                ConnectFlowServiceCreateResult,
                ConnectFlowFormState
            >(formState);
            handleServiceSuccess(response.data.message, response.data.result.id);
        }
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchConnectFlow();
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("form", {
      class: "space-y-4",
      autocomplete: "off",
      onSubmit: _withModifiers(onSubmit, ["prevent"])
    }, [
      _createVNode(_component_UiPageHeader, null, {
        info: _withCtx(() => [
          _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('connectFlow.' + props.action + '.title')), 1)
        ]),
        actions: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListConnectFlow' } }),
            (
                                (props.action == _unref(FormAction).CREATE &&
                                    _ctx.$can(_unref(Permission).CREATE_CONNECT_FLOW)) ||
                                (props.action == _unref(FormAction).EDIT &&
                                    _ctx.$can(_unref(Permission).EDIT_CONNECT_FLOW))
                            )
              ? (_openBlock(), _createBlock(_component_UiButton, {
                  key: 0,
                  type: "submit",
                  variant: "primary",
                  "text-variant": "white"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }),
      _createVNode(_component_UiPanel, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_UiTextInput, {
              modelValue: _unref(v$).name.$model,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
              name: "name",
              type: "text",
              label: _ctx.$t('connectFlow.labels.name'),
              errors: _unref(v$).name.$errors,
              required: _unref(v$).name.required !== undefined,
              onBlur: _unref(v$).name.$touch
            }, null, 8, ["modelValue", "label", "errors", "required", "onBlur"]),
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_UiCheckboxInput, {
                modelValue: _unref(v$).isActive.$model,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
                name: "isActive",
                label: _ctx.$t('connectFlow.labels.isActive')
              }, null, 8, ["modelValue", "label"])
            ])
          ])
        ]),
        _: 1
      })
    ], 32),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ]))
}
}

})